import React, { useState, useEffect } from 'react'
import {
  Save,
  RefreshCw,
  Shield,
  Bell,
  Server,
  Database,
  Lock,
  User,
  Activity,
  Download,
  Upload,
  Key,
  Globe,
  Clock,
  AlertTriangle,
} from 'lucide-react'

import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input, { Switch } from '../components/ui/Input'

interface SettingsState {
  general: {
    siteName: string
    siteDescription: string
    language: string
    timezone: string
    dateFormat: string
  }
  v2board: {
    apiUrl: string
    apiKey: string
    safeMode: boolean
    syncInterval: number
    maxRetries: number
  }
  security: {
    require2FA: boolean
    sessionTimeout: number
    maxLoginAttempts: number
    ipWhitelist: string[]
    userAgentValidation: boolean
  }
  notifications: {
    emailEnabled: boolean
    telegramEnabled: boolean
    telegramBotToken: string
    telegramChatId: string
    syncSuccess: boolean
    syncFailed: boolean
    nodeFailure: boolean
  }
  performance: {
    cacheEnabled: boolean
    cacheTTL: number
    paginationSize: number
    realtimeUpdates: boolean
    compressionEnabled: boolean
  }
  backup: {
    autoBackup: boolean
    backupInterval: number
    maxBackups: number
    cloudBackup: boolean
    backupLocation: string
  }
}

const Settings = () => {
  const [settings, setSettings] = useState<SettingsState>({
    general: {
      siteName: 'V2Board Manager',
      siteDescription: 'Sync management dashboard',
      language: 'vi',
      timezone: 'Asia/Ho_Chi_Minh',
      dateFormat: 'DD/MM/YYYY HH:mm',
    },
    v2board: {
      apiUrl: 'https://your-v2board.com',
      apiKey: '',
      safeMode: true,
      syncInterval: 30,
      maxRetries: 3,
    },
    security: {
      require2FA: false,
      sessionTimeout: 60,
      maxLoginAttempts: 5,
      ipWhitelist: [''],
      userAgentValidation: true,
    },
    notifications: {
      emailEnabled: true,
      telegramEnabled: false,
      telegramBotToken: '',
      telegramChatId: '',
      syncSuccess: true,
      syncFailed: true,
      nodeFailure: true,
    },
    performance: {
      cacheEnabled: true,
      cacheTTL: 300,
      paginationSize: 50,
      realtimeUpdates: true,
      compressionEnabled: true,
    },
    backup: {
      autoBackup: true,
      backupInterval: 24,
      maxBackups: 30,
      cloudBackup: false,
      backupLocation: './backups',
    },
  })

  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('general')

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Save settings to API
      await new Promise(resolve => setTimeout(resolve, 1000))
      console.log('Settings saved:', settings)
      alert('Settings saved successfully!')
    } catch (error) {
      console.error('Error saving settings:', error)
      alert('Error saving settings')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    if (confirm('Are you sure you want to reset all settings to default?')) {
      // Reset to default values
      setSettings({
        general: {
          siteName: 'V2Board Manager',
          siteDescription: 'Sync management dashboard',
          language: 'vi',
          timezone: 'Asia/Ho_Chi_Minh',
          dateFormat: 'DD/MM/YYYY HH:mm',
        },
        v2board: {
          apiUrl: 'https://your-v2board.com',
          apiKey: '',
          safeMode: true,
          syncInterval: 30,
          maxRetries: 3,
        },
        security: {
          require2FA: false,
          sessionTimeout: 60,
          maxLoginAttempts: 5,
          ipWhitelist: [''],
          userAgentValidation: true,
        },
        notifications: {
          emailEnabled: true,
          telegramEnabled: false,
          telegramBotToken: '',
          telegramChatId: '',
          syncSuccess: true,
          syncFailed: true,
          nodeFailure: true,
        },
        performance: {
          cacheEnabled: true,
          cacheTTL: 300,
          paginationSize: 50,
          realtimeUpdates: true,
          compressionEnabled: true,
        },
        backup: {
          autoBackup: true,
          backupInterval: 24,
          maxBackups: 30,
          cloudBackup: false,
          backupLocation: './backups',
        },
      })
    }
  }

  const handleTestV2BoardConnection = async () => {
    try {
      // Test V2Board connection
      console.log('Testing V2Board connection...')
      await new Promise(resolve => setTimeout(resolve, 500))
      alert('V2Board connection test successful!')
    } catch (error) {
      console.error('Connection test failed:', error)
      alert('Connection test failed')
    }
  }

  const handleBackupNow = async () => {
    try {
      // Trigger backup
      console.log('Creating backup...')
      await new Promise(resolve => setTimeout(resolve, 1000))
      alert('Backup created successfully!')
    } catch (error) {
      console.error('Backup failed:', error)
      alert('Backup failed')
    }
  }

  const tabs = [
    { id: 'general', label: 'General', icon: <User className="w-4 h-4" /> },
    { id: 'v2board', label: 'V2Board', icon: <Server className="w-4 h-4" /> },
    { id: 'security', label: 'Security', icon: <Shield className="w-4 h-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
    { id: 'performance', label: 'Performance', icon: <Activity className="w-4 h-4" /> },
    { id: 'backup', label: 'Backup', icon: <Database className="w-4 h-4" /> },
  ]

  const renderContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Application Settings
                </h3>
                <Input
                  label="Site Name"
                  value={settings.general.siteName}
                  onChange={(e) => setSettings(prev => ({ ...prev, general: { ...prev.general, siteName: e.target.value } }))}
                />
                <Input
                  label="Site Description"
                  value={settings.general.siteDescription}
                  onChange={(e) => setSettings(prev => ({ ...prev, general: { ...prev.general, siteDescription: e.target.value } }))}
                  type="textarea"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Language"
                    value={settings.general.language}
                    onChange={(e) => setSettings(prev => ({ ...prev, general: { ...prev.general, language: e.target.value } }))}
                  />
                  <Input
                    label="Timezone"
                    value={settings.general.timezone}
                    onChange={(e) => setSettings(prev => ({ ...prev, general: { ...prev.general, timezone: e.target.value } }))}
                  />
                </div>
                <Input
                  label="Date Format"
                  value={settings.general.dateFormat}
                  onChange={(e) => setSettings(prev => ({ ...prev, general: { ...prev.general, dateFormat: e.target.value } }))}
                />
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Application Info
                </h3>
                <div className="space-y-4 p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">Version</p>
                    <p className="font-medium">1.0.0</p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">Build Date</p>
                    <p className="font-medium">2024-05-11</p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">Environment</p>
                    <p className="font-medium">Production</p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">License</p>
                    <p className="font-medium">MIT</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 'v2board':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
              <Server className="w-5 h-5" />
              V2Board Integration
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Input
                    label="API URL"
                    value={settings.v2board.apiUrl}
                    onChange={(e) => setSettings(prev => ({ ...prev, v2board: { ...prev.v2board, apiUrl: e.target.value } }))}
                  />
                  <Input
                    label="API Key"
                    value={settings.v2board.apiKey}
                    onChange={(e) => setSettings(prev => ({ ...prev, v2board: { ...prev.v2board, apiKey: e.target.value } }))}
                    type="password"
                  />
                  <div className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                    <div>
                      <p className="font-medium">Safe Mode</p>
                      <p className="text-sm text-secondary-500 dark:text-secondary-400">
                        Only create nodes in separate groups
                      </p>
                    </div>
                    <Switch
                      checked={settings.v2board.safeMode}
                      onChange={(checked) => setSettings(prev => ({ ...prev, v2board: { ...prev.v2board, safeMode: checked } }))}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-secondary-900 dark:text-white">
                      Sync Interval (minutes)
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="1440"
                      step="5"
                      value={settings.v2board.syncInterval}
                      onChange={(e) => setSettings(prev => ({ ...prev, v2board: { ...prev.v2board, syncInterval: parseInt(e.target.value) } }))}
                      className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer dark:bg-secondary-700"
                    />
                    <div className="flex justify-between text-sm text-secondary-500 dark:text-secondary-400">
                      <span>5 min</span>
                      <span>{settings.v2board.syncInterval} min</span>
                      <span>24h</span>
                    </div>
                  </div>
                  <Input
                    label="Max Retries"
                    type="number"
                    min="1"
                    max="10"
                    value={settings.v2board.maxRetries.toString()}
                    onChange={(e) => setSettings(prev => ({ ...prev, v2board: { ...prev.v2board, maxRetries: parseInt(e.target.value) } }))}
                  />
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <Button
                  onClick={handleTestV2BoardConnection}
                  variant="secondary"
                  leftIcon={<RefreshCw className="w-4 h-4" />}
                >
                  Test Connection
                </Button>
                <Button
                  onClick={() => alert('Manual sync triggered')}
                  variant="secondary"
                  leftIcon={<Clock className="w-4 h-4" />}
                >
                  Sync Now
                </Button>
              </div>
            </div>
          </div>
        )

      case 'security':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Security Settings
            </h3>
            <div className="space-y-4">
              <div className="space-y-4 p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Require 2FA</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      Enable two-factor authentication for all users
                    </p>
                  </div>
                  <Switch
                    checked={settings.security.require2FA}
                    onChange={(checked) => setSettings(prev => ({ ...prev, security: { ...prev.security, require2FA: checked } }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">User Agent Validation</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      Validate browser user agent for security
                    </p>
                  </div>
                  <Switch
                    checked={settings.security.userAgentValidation}
                    onChange={(checked) => setSettings(prev => ({ ...prev, security: { ...prev.security, userAgentValidation: checked } }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Input
                    label="Session Timeout (minutes)"
                    type="number"
                    min="5"
                    max="1440"
                    value={settings.security.sessionTimeout.toString()}
                    onChange={(e) => setSettings(prev => ({ ...prev, security: { ...prev.security, sessionTimeout: parseInt(e.target.value) } }))}
                  />
                  <Input
                    label="Max Login Attempts"
                    type="number"
                    min="1"
                    max="10"
                    value={settings.security.maxLoginAttempts.toString()}
                    onChange={(e) => setSettings(prev => ({ ...prev, security: { ...prev.security, maxLoginAttempts: parseInt(e.target.value) } }))}
                  />
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-900 dark:text-white mb-2">
                      IP Whitelist (one per line)
                    </label>
                    <textarea
                      value={settings.security.ipWhitelist.join('\n')}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        security: {
                          ...prev.security,
                          ipWhitelist: e.target.value.split('\n').filter(ip => ip.trim())
                        }
                      }))}
                      className="w-full px-4 py-2 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      rows={4}
                    />
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-lg border border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-900/20">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-warning-900 dark:text-warning-100">
                      Security Note
                    </p>
                    <p className="text-sm text-warning-700 dark:text-warning-300 mt-1">
                      Changing security settings may affect user access. Ensure you understand the implications before saving.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 'notifications':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notification Settings
            </h3>
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      Send notifications via email
                    </p>
                  </div>
                  <Switch
                    checked={settings.notifications.emailEnabled}
                    onChange={(checked) => setSettings(prev => ({ ...prev, notifications: { ...prev.notifications, emailEnabled: checked } }))}
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div>
                    <p className="font-medium">Telegram Notifications</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      Send notifications via Telegram bot
                    </p>
                  </div>
                  <Switch
                    checked={settings.notifications.telegramEnabled}
                    onChange={(checked) => setSettings(prev => ({ ...prev, notifications: { ...prev.notifications, telegramEnabled: checked } }))}
                  />
                </div>
              </div>
              {settings.notifications.telegramEnabled && (
                <div className="space-y-4 p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <h4 className="font-medium text-secondary-900 dark:text-white">Telegram Configuration</h4>
                  <Input
                    label="Bot Token"
                    value={settings.notifications.telegramBotToken}
                    onChange={(e) => setSettings(prev => ({ ...prev, notifications: { ...prev.notifications, telegramBotToken: e.target.value } }))}
                    type="password"
                  />
                  <Input
                    label="Chat ID"
                    value={settings.notifications.telegramChatId}
                    onChange={(e) => setSettings(prev => ({ ...prev, notifications: { ...prev.notifications, telegramChatId: e.target.value } }))}
                  />
                </div>
              )}
              <div className="space-y-3">
                <h4 className="font-medium text-secondary-900 dark:text-white">Notification Types</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Sync Success</p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        Notify when sync completes successfully
                      </p>
                    </div>
                    <Switch
                      checked={settings.notifications.syncSuccess}
                      onChange={(checked) => setSettings(prev => ({ ...prev, notifications: { ...prev.notifications, syncSuccess: checked } }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Sync Failed</p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        Notify when sync fails
                      </p>
                    </div>
                    <Switch
                      checked={settings.notifications.syncFailed}
                      onChange={(checked) => setSettings(prev => ({ ...prev, notifications: { ...prev.notifications, syncFailed: checked } }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Node Failure</p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        Notify when nodes fail to connect
                      </p>
                    </div>
                    <Switch
                      checked={settings.notifications.nodeFailure}
                      onChange={(checked) => setSettings(prev => ({ ...prev, notifications: { ...prev.notifications, nodeFailure: checked } }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 'performance':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Performance Settings
            </h3>
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div>
                    <p className="font-medium">Cache Enabled</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      Enable caching for improved performance
                    </p>
                  </div>
                  <Switch
                    checked={settings.performance.cacheEnabled}
                    onChange={(checked) => setSettings(prev => ({ ...prev, performance: { ...prev.performance, cacheEnabled: checked } }))}
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div>
                    <p className="font-medium">Realtime Updates</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      Enable WebSocket for realtime updates
                    </p>
                  </div>
                  <Switch
                    checked={settings.performance.realtimeUpdates}
                    onChange={(checked) => setSettings(prev => ({ ...prev, performance: { ...prev.performance, realtimeUpdates: checked } }))}
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div>
                    <p className="font-medium">Compression</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      Compress API responses
                    </p>
                  </div>
                  <Switch
                    checked={settings.performance.compressionEnabled}
                    onChange={(checked) => setSettings(prev => ({ ...prev, performance: { ...prev.performance, compressionEnabled: checked } }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Input
                    label="Cache TTL (seconds)"
                    type="number"
                    min="10"
                    max="86400"
                    value={settings.performance.cacheTTL.toString()}
                    onChange={(e) => setSettings(prev => ({ ...prev, performance: { ...prev.performance, cacheTTL: parseInt(e.target.value) } }))}
                  />
                  <Input
                    label="Pagination Size"
                    type="number"
                    min="10"
                    max="500"
                    value={settings.performance.paginationSize.toString()}
                    onChange={(e) => setSettings(prev => ({ ...prev, performance: { ...prev.performance, paginationSize: parseInt(e.target.value) } }))}
                  />
                </div>
                <div className="space-y-4 p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <h4 className="font-medium text-secondary-900 dark:text-white">Performance Tips</h4>
                  <ul className="space-y-2 text-sm text-secondary-600 dark:text-secondary-300">
                    <li className="flex items-start gap-2">
                      <span className="text-primary-600 dark:text-primary-400">•</span>
                      <span>Enable cache for read-heavy operations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-600 dark:text-primary-400">•</span>
                      <span>Use moderate pagination sizes (50-100)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-600 dark:text-primary-400">•</span>
                      <span>Compress responses for mobile usage</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-600 dark:text-primary-400">•</span>
                      <span>Scheduled syncs reduce server load</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )

      case 'backup':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
              <Database className="w-5 h-5" />
              Backup Settings
            </h3>
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div>
                    <p className="font-medium">Auto Backup</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      Automatically create database backups
                    </p>
                  </div>
                  <Switch
                    checked={settings.backup.autoBackup}
                    onChange={(checked) => setSettings(prev => ({ ...prev, backup: { ...prev.backup, autoBackup: checked } }))}
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 dark:border-secondary-700">
                  <div>
                    <p className="font-medium">Cloud Backup</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      Sync backups to cloud storage
                    </p>
                  </div>
                  <Switch
                    checked={settings.backup.cloudBackup}
                    onChange={(checked) => setSettings(prev => ({ ...prev, backup: { ...prev.backup, cloudBackup: checked } }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Input
                    label="Backup Interval (hours)"
                    type="number"
                    min="1"
                    max="720"
                    value={settings.backup.backupInterval.toString()}
                    onChange={(e) => setSettings(prev => ({ ...prev, backup: { ...prev.backup, backupInterval: parseInt(e.target.value) } }))}
                  />
                  <Input
                    label="Max Backups"
                    type="number"
                    min="1"
                    max="100"
                    value={settings.backup.maxBackups.toString()}
                    onChange={(e) => setSettings(prev => ({ ...prev, backup: { ...prev.backup, maxBackups: parseInt(e.target.value) } }))}
                  />
                  <Input
                    label="Backup Location"
                    value={settings.backup.backupLocation}
                    onChange={(e) => setSettings(prev => ({ ...prev, backup: { ...prev.backup, backupLocation: e.target.value } }))}
                  />
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border border-secondary-200 dark:border-secondary-700 space-y-3">
                    <h4 className="font-medium text-secondary-900 dark:text-white">Quick Backup</h4>
                    <p className="text-sm text-secondary-600 dark:text-secondary-300">
                      Create a manual backup of the current database. This will include all providers, sync logs, and user data.
                    </p>
                    <Button
                      onClick={handleBackupNow}
                      variant="secondary"
                      leftIcon={<Download className="w-4 h-4" />}
                      fullWidth
                    >
                      Backup Now
                    </Button>
                  </div>
                  <div className="p-4 rounded-lg border border-secondary-200 dark:border-secondary-700 space-y-3">
                    <h4 className="font-medium text-secondary-900 dark:text-white">Restore Backup</h4>
                    <Button
                      onClick={() => alert('Restore backup dialog')}
                      variant="secondary"
                      leftIcon={<Upload className="w-4 h-4" />}
                      fullWidth
                    >
                      Restore from Backup
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Settings
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400 mt-1">
            Configure your V2Board manager settings
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleReset}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            leftIcon={isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            isLoading={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Card className="overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          {/* Sidebar Tabs */}
          <div className="lg:w-64 border-b lg:border-b-0 lg:border-r border-secondary-200 dark:border-secondary-700">
            <div className="p-4 lg:p-6">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === tab.id
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700'
                        : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-800'
                      }`}
                  >
                    {tab.icon}
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1">
            <div className="p-4 lg:p-6">
              {renderContent()}
            </div>
          </div>
        </div>
      </Card>

      {/* Important Notes */}
      <Card>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-warning-50 dark:bg-warning-900/30 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400" />
          </div>
          <div>
            <h3 className="font-medium text-secondary-900 dark:text-white">
              Important Notes
            </h3>
            <ul className="space-y-2 text-sm text-secondary-600 dark:text-secondary-300 mt-2">
              <li className="flex items-start gap-2">
                <span className="text-warning-600 dark:text-warning-400 mt-1">•</span>
                <span>All settings are validated before being saved to ensure system stability</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-warning-600 dark:text-warning-400 mt-1">•</span>
                <span>Some changes may require restarting the application (marked with icon)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-warning-600 dark:text-warning-400 mt-1">•</span>
                <span>Always test V2Board connection after changing integration settings</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-warning-600 dark:text-warning-400 mt-1">•</span>
                <span>Backups are automatically generated but should also be stored in secondary location</span>
              </li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default Settings
